import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { extractErrorMessage } from "utils/errorHandler";

export type HourlyLeavePayload = {
  leaveType: string;
  reason: string;
  date: string;
  hours: number;
  departmentId: string;
};

export interface HourlyLeaveData {
  _id: string;
  leaveType: string;
  reason: string;
  date: string;
  hours: number;
  departmentId: string;
  requestedBy?: string;
  status?: "pending" | "approved" | "rejected" | string;
  createdAt?: string;
  updatedAt?: string;
}

export type HourlyLeaveResponse = {
  success: boolean;
  message: string;
  data: HourlyLeaveData;
};

const createHourlyLeave = async (
  payload: HourlyLeavePayload
): Promise<HourlyLeaveResponse> => {
  const { data } = await customAxios.post<HourlyLeaveResponse>(
    "/leaves/hourly-request",
    payload
  );
  return data;
};

export const useHourlyLeaveRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<HourlyLeaveResponse, AxiosError, HourlyLeavePayload>({
    mutationFn: createHourlyLeave,
    onSuccess: async (data) => {
      showNotification({
        title: "Leave requested",
        message: data.message,
        color: "green",
      });

      await queryClient.invalidateQueries(["leaves"]);
      await queryClient.invalidateQueries(["hourly-requests"]);
    },
    onError: (error) => {
      showNotification({
        title: "Hourly Leave Failed",
        message: extractErrorMessage(error, "Failed to request hourly leave"),
        color: "red",
        autoClose: 8000, // Show for 8 seconds for longer messages
      });
    },
  });
};
